import React, { useContext, useCallback, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { MessageType } from "src/components/notifications/Notifier";
import styles from "./CurrentAgendaItemVoting.module.css";

const CurrentAgendaItemVotingBase = () => {
    const { t } = useTranslation();
    const { uiStore, agendaitemStore, votingStore } = useContext(RootStoreContext);

    const [showDeleteVotingModal, setShowDeleteVotingModal] = useState<boolean>(false);
    const [deleteVotingId, setDeleteVotingId] = useState<string>("");

    useEffect(() => {
        if (agendaitemStore.currentAgendaItem?.id) {
            votingStore.loadAllVotingsByAgendaItemId(agendaitemStore.currentAgendaItem.id);
        }
    }, [agendaitemStore.currentAgendaItem, votingStore]);

    const printStatusMessage = useCallback(
        (message: string, msgType: MessageType) => {
            uiStore.enqueueSnackbar({
                content: message,
                options: {
                    type: msgType,
                    autoClose: msgType === MessageType.ERROR ? false : 3000
                }
            });
        },
        [uiStore]
    );

    const onClickTashIcon = useCallback(
        (votingid: string) => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            setShowDeleteVotingModal(true);
            setDeleteVotingId(votingid);
        },
        []
    );

    const onClickVoting = useCallback(
        (votingid: string) => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            votingStore.setSelectedVotingId(votingid);
            await votingStore.updateVotingBasisField("votingId", votingid);
            votingStore.setIsNewVoting(false);
            votingStore.setIsVotingModalDisplayed(true);
        },
        [votingStore]
    );

    const onDeleteVoting = useCallback(
        () => async (e: React.MouseEvent<HTMLSpanElement>) => {
            e.preventDefault();
            if (votingStore.votingList.length > 0) {
                votingStore.deleteVotingById(deleteVotingId);
                printStatusMessage(t("screens.assemblies.agenda.voting_delete_success"), MessageType.SUCCESS);
                setShowDeleteVotingModal(false);
            }
        },
        [deleteVotingId, votingStore, printStatusMessage, t]
    );

    const onCloseModal = useCallback(async () => {
        setShowDeleteVotingModal(false);
    }, []);

    return (
        <div className="mb-4">
            {votingStore.votingList.map((voting) => {
                return (
                    <div className={styles.ListItem} key={voting.votingid}>
                        <div className={styles.VotingItemsContainer} onClick={onClickVoting(voting.votingid)}>
                            <UI.Icon icon={UI.SVGIcon.Voting} size={32} className={styles.VotingIcon} />
                            <div className={styles.VotingDetailsRow}>
                                <div className={styles.VotingTitleQuestion}>
                                    {voting.title}: {voting.question}
                                </div>
                            </div>
                        </div>
                        <div className={styles.AdditionalIcon}>
                            <UI.Icon
                                className={styles.TrashIcon}
                                icon={UI.SVGIcon.Trash}
                                onClick={onClickTashIcon(voting.votingid)}
                            />
                        </div>
                    </div>
                );
            })}
            <UI.Modal show={showDeleteVotingModal} onClose={onCloseModal} title={t("labels.delete").toString()}>
                <div>{t("screens.assemblies.agenda.voting_delete_question")}</div>
                <div className={styles.ButtonRow}>
                    <UI.Button label={t("labels.yes")} onClick={onDeleteVoting()} className={styles.ButtonModal} />
                    <UI.Button label={t("labels.no")} onClick={onCloseModal} className={styles.ButtonModal} />
                </div>
            </UI.Modal>
        </div>
    );
};

export const CurrentAgendaItemVoting = Sentry.withProfiler(observer(CurrentAgendaItemVotingBase));
